import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    CircularProgress,
    Alert,
    Container
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Email as EmailIcon,
    Phone as PhoneIcon,
    Lock as LockIcon,
    ChevronRight as ChevronRightIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import { useFormik } from 'formik';
import axios from 'src/services/ApiService';
import * as Yup from 'yup';
import AuthorizeService from 'src/components/api-authorization/AuthorizeService';
import toast from 'react-hot-toast'
import InputAdornment from '@mui/material/InputAdornment';

// Styled button for preferences
type PreferenceButtonProps = React.ComponentProps<typeof Button>;
const PreferenceButton = styled((props: PreferenceButtonProps) => <Button {...props} />)(({ theme }) => ({
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    textTransform: 'none',
    width: '100%',
    color: theme.palette.text.primary,
    '& .MuiButton-endIcon': { marginLeft: 'auto' }
}));

// Form state type
interface FormState {
    firstName: string;
    lastName: string;
    email: string;
    imgSrc: string;
    phoneNumber: string;
}

const initialFormState: FormState = {
    firstName: '',
    lastName: '',
    email: '',
    imgSrc: '/images/avatars/1.png',
    phoneNumber: ''
};

const validationSchema = Yup.object({
    firstName: Yup.string().required('First Name is Required'),
    lastName: Yup.string().required('Last Name is Required'),
    email: Yup.string().email('Please enter a valid email address').required('Email is Required')
});

const SignInSecurity: React.FC = () => {
    const [imgSrc, setImgSrc] = useState<string>(initialFormState.imgSrc);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [successOpen, setSuccessOpen] = useState<boolean>(false);
    const [userId, setUserId] = useState<string | null>(null);
    const [resetPhoneDialog, setResetPhoneDialog] = useState<boolean>(false);
    const [resetPasswordDialog, setResetPasswordDialog] = useState<boolean>(false);
    const [resetEmailDialog, setResetEmailDialog] = useState<boolean>(false);
    const [passwordSuccessOpen, setPasswordSuccessOpen] = useState<boolean>(false);

    const [newPassword, setNewPassword] = useState<string>('');
    const [newPasswordTwice, setNewPasswordTwice] = useState<string>('');
    const [currentPassword, setCurrentPassword] = useState<string>('');

    const [passwordMismatch, setPasswordMismatch] = useState<boolean>(false);
    const [passwordShouldNotMatch, setPasswordShouldNotMatch] = useState<boolean>(false);
    const [currentPasswordIncorrect, setCurrentPasswordIncorrect] = useState<boolean>(false);
    const [formNotFilled, setFormNotFilled] = useState<boolean>(true);
    const [isFirstPasswordSet, setIsFirstPasswordSet] = useState<boolean>(true);

    // Passwordless states
    const [showVerificationBoxes, setShowVerificationBoxes] = useState<boolean>(false);
    const [verificationCode, setVerificationCode] = useState<string[]>(Array(6).fill(''));
    const [verificationStatus, setVerificationStatus] = useState<'valid' | 'invalid' | 'expired' | null>(null);
    const [isVerifyingCode, setIsVerifyingCode] = useState<boolean>(false);
    const [isVerificationCompleted, setIsVerificationCompleted] = useState<boolean>(false);
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
    const [otpMessage, setOtpMessage] = useState<string | null>(null);
    const [codeSent, setCodeSent] = useState<boolean>(false);
    const [cooldown, setCooldown] = useState<number>(0);   // 0 = button is live
    const cooldownRef = useRef<NodeJS.Timeout | null>(null);
    const [sendingCode, setSendingCode] = useState<boolean>(false);


    // Fetch user profile
    useEffect(() => {
        (async () => {
            try {
                const response = await axios.get('userprofile/GetCurrentUserProfile');
                const { firstName, lastName, email, imgSrc, phoneNumber, isFirstPasswordSet } = response.data.result;
                formik.setValues({ firstName, lastName, email, imgSrc, phoneNumber });
                setIsFirstPasswordSet(isFirstPasswordSet);
                setImgSrc(imgSrc);
            } catch (error) {
                console.error('Failed to fetch user profile:', error);
            }
        })();
        (async () => {
            const response = await AuthorizeService.getUser();
            setUserId(response ? response.sub : null);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Formik for email/phone
    const formik = useFormik<FormState>({
        initialValues: initialFormState,
        validationSchema,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            try {
                await axios.post('userprofile/SaveCurrentUserProfile', { ...values, imgSrc });
                setSuccessOpen(true);
            } catch (error) {
                console.error('Save profile error:', error);
            }
            setIsSubmitting(false);
        }
    });

    // Enable save
    useEffect(() => {
        const allNew = newPassword !== '' && newPasswordTwice !== '';
        let ok: boolean;

        if (isFirstPasswordSet) {
            if (codeSent) {
                ok = isVerificationCompleted && allNew;
            } else {
                ok = currentPassword !== '' && allNew;
            }
        } else {
            ok = allNew;//isVerificationCompleted && allNew;
        }

        setFormNotFilled(!ok);
        //const ok = isFirstPasswordSet
        //    ? currentPassword !== '' && allNew
        //    : isVerificationCompleted && allNew;
        //setFormNotFilled(!ok);
    }, [currentPassword, newPassword, newPasswordTwice, isFirstPasswordSet, isVerificationCompleted, codeSent]);

    // Auto verify
    useEffect(() => {
        if (showVerificationBoxes && verificationCode.every(d => d !== '')) {
            verifyCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verificationCode]);

    // Reset password
    const resetPassword = async () => {
        if (currentPassword === newPassword) {
            setPasswordShouldNotMatch(true);
            return;
        }
        if (newPassword !== newPasswordTwice) {
            setPasswordMismatch(true);
            return;
        }
        setIsSubmitting(true);
        try {
            const { data } = await axios.post('userprofile/ChangePassword', { userId, newPassword, currentPassword, otpCode: verificationCode.join('') });
            if (data === false) {
                setCurrentPasswordIncorrect(true);
                setIsSubmitting(false);
                return;
            }
            // success
            setCurrentPasswordIncorrect(false);
            setIsFirstPasswordSet(true);
            setCurrentPassword('');
            setNewPassword('');
            setNewPasswordTwice('');
            setPasswordSuccessOpen(true);
            setResetPasswordDialog(false);
            clear();
        } catch (error) {
            console.error('Change password error:', error);
        }
        setIsSubmitting(false);
    };

    const sendCode = async () => {
        try {
            if (cooldown > 0 || sendingCode) return;
            setSendingCode(true);
            setShowVerificationBoxes(false);
            setVerificationStatus(null);
            const { data } = await axios.post('userprofile/PasswordlessLogin');
            if (data === 'success') {
                setCodeSent(true);
                setShowVerificationBoxes(true); 

                if (cooldownRef.current) clearInterval(cooldownRef.current);
                setCooldown(60);
                cooldownRef.current = setInterval(() => {
                    setCooldown(prev => {
                        if (prev <= 1) {
                            clearInterval(cooldownRef.current!);
                            return 0;                   
                        }
                        return prev - 1;
                    });
                }, 1000);

            }
        } catch (err) {
            console.error('Send code error:', err);
        }
        finally {
            setSendingCode(false);
        }
    };

    const verifyCode = async () => {
        setIsVerifyingCode(true);

        try {
            const { data } = await axios.post(
                '/userprofile/PasswordLessAuthenticate',
                { code: verificationCode.join('') },
            );

            switch (data) {
                case 'success':
                    setVerificationStatus('valid');
                    setIsVerificationCompleted(true);
                    break;

                case 'otpexpired':
                    setVerificationStatus('expired');
                    setIsVerificationCompleted(false);
                    setOtpMessage('The code has expired. Please request a new one.');
                    break;

                default: 
                    setVerificationStatus('invalid');
                    setIsVerificationCompleted(false);
                    setOtpMessage('OTP is not correct!');

            }

            if (data !== 'success') {
                setTimeout(() => setOtpMessage(null), 5000);
            }

        } catch (err) {
            console.error('Verify code error:', err);
            setVerificationStatus('invalid');
            setIsVerificationCompleted(false);
            setOtpMessage('Something went wrong — try again.');
            setTimeout(() => setOtpMessage(null), 5000);

        } finally {
            setIsVerifyingCode(false);
        }
    };

    const clear = () => {
        setCurrentPassword('');
        setNewPassword('');
        setNewPasswordTwice('');
        setShowVerificationBoxes(false);
        setVerificationStatus(null);
        setPasswordMismatch(false);
        setPasswordShouldNotMatch(false);
        setCurrentPasswordIncorrect(false);
        setFormNotFilled(true);
        setCodeSent(false);
        setVerificationCode(Array(6).fill(''));

    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const paste = e.clipboardData.getData('Text').trim();
        if (/^\d{1,6}$/.test(paste)) {
            const digits = paste.split('').slice(0, 6);
            setVerificationCode([...digits, ...Array(6 - digits.length).fill('')]);
            setVerificationStatus(null);
        }
    };

    return (
        <Container sx={{ maxWidth: 'xl', mx: 'auto', p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 2 }}>
                Sign in and security
            </Typography>
            <Typography variant="body2" sx={{ mb: 4 }}>
                Manage your account access and security settings
            </Typography>
            <Card>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <PreferenceButton
                                startIcon={<EmailIcon />} endIcon={<ChevronRightIcon />}
                                onClick={() => setResetEmailDialog(true)}
                            >Change Email</PreferenceButton>
                        </Grid>
                        <Grid item xs={12}>
                            <PreferenceButton
                                startIcon={<PhoneIcon />} endIcon={<ChevronRightIcon />}
                                onClick={() => setResetPhoneDialog(true)}
                            >Phone Number</PreferenceButton>
                        </Grid>
                        <Grid item xs={12}>
                            <PreferenceButton
                                startIcon={<LockIcon />} endIcon={<ChevronRightIcon />}
                                onClick={() => setResetPasswordDialog(true)}
                            >Change Password</PreferenceButton>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Dialog open={resetEmailDialog} onClose={() => setResetEmailDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Change Email Address
                    <IconButton onClick={() => setResetEmailDialog(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth label="Email" name="email"
                                    value={formik.values.email} onChange={formik.handleChange}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                    variant="outlined" size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button type="submit" variant="contained" disabled={isSubmitting}>
                                    {isSubmitting ? <CircularProgress size={24} /> : 'Change Email'}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Phone Dialog */}
            <Dialog open={resetPhoneDialog} onClose={() => setResetPhoneDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Change Phone Number
                    <IconButton onClick={() => setResetPhoneDialog(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth label="Phone Number" name="phoneNumber"
                                    value={formik.values.phoneNumber} onChange={formik.handleChange}
                                    error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                                    helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                                    variant="outlined" size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button type="submit" variant="contained" disabled={isSubmitting}>
                                    {isSubmitting ? <CircularProgress size={24} /> : 'Save Changes'}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Change Password Dialog */}
            <Dialog open={resetPasswordDialog} onClose={() => { setResetPasswordDialog(false); clear(); }} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Change Password
                    <IconButton onClick={() => { setResetPasswordDialog(false); clear(); }} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        {isFirstPasswordSet && (
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center">
                                    <TextField
                                        fullWidth label="Current Password" type="password"
                                        value={currentPassword} onChange={e => { setCurrentPassword(e.target.value); setPasswordMismatch(false); setPasswordShouldNotMatch(false); setCurrentPasswordIncorrect(false); }}
                                        onPaste={handlePaste}
                                        variant="outlined"
                                        disabled={codeSent}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end" sx={{ pl: 1 }}>
                                                    {verificationStatus === 'valid' ? (
                                                        <CheckIcon sx={{ color: 'green', mr: 1 }} />
                                                    ) : verificationStatus === 'invalid' ? (
                                                        <CancelIcon sx={{ color: 'red', mr: 1 }} />
                                                    ) : null}


                                                    <Button
                                                        onClick={sendCode}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            textTransform: 'none',
                                                            minWidth: 80,
                                                            fontSize: '0.75rem',
                                                            py: 0.5   
                                                        }}
                                                        disabled={cooldown > 0}
                                                    >
                                                      {sendingCode ? (
                                                        <CircularProgress size={16} />               
                                                      ) : cooldown > 0 ? (
                                                        `${cooldown}s`
                                                      ) : (
                                                        'Send Code'
                                                      )}
                                                    </Button>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Box>
                            </Grid>
                        )}

                        {showVerificationBoxes && (
                            <>
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>
                                    Please enter the code we sent to your email address.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" gap={2} justifyContent="center" mt={2} >
                                    {verificationCode.map((val, idx) => (
                                        <TextField
                                            key={idx}
                                            value={val}
                                            onChange={e => {
                                                const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
                                                const arr = [...verificationCode];
                                                arr[idx] = v;
                                                setVerificationCode(arr);
                                                setVerificationStatus(null);
                                                 if (v && idx < verificationCode.length - 1) {
                                                    inputsRef.current[idx + 1]?.focus();
                                                }
                                            }}
                                            inputRef={el => (inputsRef.current[idx] = el)}
                                            inputProps={{
                                                maxLength: 1,
                                                style: {
                                                    textAlign: 'center',
                                                    fontSize: '1.25rem',
                                                }
                                            }}
                                            variant="outlined"
                                            sx={{
                                                width: 40,
                                                height: 48,
                                                '& .MuiOutlinedInput-root': {
                                                    p: 0,
                                                    height: 1,                        
                                                    borderRadius: 1.5,
                                                    backgroundColor: '#f4f6f8'
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': { top: 0 },
                                                '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' }
                                            }}
                                            onPaste={handlePaste}
                                            InputProps={{ notched: false }}

                                        />
                                    ))}
                                </Box>
                                </Grid>
                                {cooldown > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                                            You can request a new code in&nbsp;<strong>{cooldown}</strong>&nbsp;seconds
                                        </Typography>
                                    </Grid>
                                )}
                            </>
                        )}

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth label="New Password" type="password"
                                value={newPassword} onChange={e => { setNewPassword(e.target.value); setPasswordMismatch(false); }}
                                variant="outlined" size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth label="Confirm New Password" type="password"
                                value={newPasswordTwice} onChange={e => { setNewPasswordTwice(e.target.value); setPasswordMismatch(false); }}
                                variant="outlined" size="small"
                            />
                        </Grid>

                        {passwordMismatch && <Grid item xs={12}><Alert severity="error">Passwords do not match</Alert></Grid>}
                        {passwordShouldNotMatch && <Grid item xs={12}><Alert severity="error">Current password must differ from new password</Alert></Grid>}
                        {currentPasswordIncorrect && <Grid item xs={12}><Alert severity="error">Current password is incorrect</Alert></Grid>}
                        {formNotFilled && <Grid item xs={12}><Alert severity="warning">All fields must be filled properly</Alert></Grid>}
                        {otpMessage && (
                            <Grid item xs={12}>
                                <Alert severity="error">{otpMessage}</Alert>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Button variant="contained" onClick={resetPassword} disabled={formNotFilled || isSubmitting}>
                                {isSubmitting ? <CircularProgress size={24} /> : 'Save Changes'}
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            <Dialog open={successOpen} onClose={() => setSuccessOpen(false)}>
                <DialogTitle>Success</DialogTitle>
                <DialogContent><Typography>Profile updated successfully!</Typography></DialogContent>
                <DialogActions><Button onClick={() => setSuccessOpen(false)}>Close</Button></DialogActions>
            </Dialog>

            <Dialog open={passwordSuccessOpen} onClose={() => setPasswordSuccessOpen(false)}>
                <DialogTitle>Success</DialogTitle>
                <DialogContent><Typography>Password updated successfully!</Typography></DialogContent>
                <DialogActions><Button onClick={() => setPasswordSuccessOpen(false)}>Close</Button></DialogActions>
            </Dialog>
        </Container>
    );
};

export default SignInSecurity;
